import { useEffect } from 'react';
import { Platform } from 'react-native';
import { Tabs } from 'expo-router';

import { AppDrawer } from '@/components/app-drawer';
// Tab icons: the previously hand-rolled components were lucide marks
// drawn by hand (message-square, users, user) -- now from the library.
// Users (not a heart) for Matches: the heart reads romantic/dating-app,
// wrong for a neighbor-focused product (see design.md's "What this
// is"). Stroke-based, color passed through, so the bar's active/
// inactive tint still swaps with dark mode with no light/dark branch.
import { MessageSquare as ThreadIcon, User as ProfileIcon, Users as MatchesIcon } from 'lucide-react-native';
import { DockIcon, useDockHeight, useTabBarScreenOptions } from '@/components/tab-bar';
import { iconSize, iconStroke } from '@/theme/icon';
import { DockInsetContext, setDockHideDistance } from '@/lib/dock';
import { TabBarDebugOverlay } from '@/components/tab-bar-debug-overlay';
import { AppDrawerProvider } from '@/hooks/use-app-drawer';
import { useActivity } from '@/hooks/use-activity';
import { useMyProfile } from '@/hooks/use-my-profile';
import { ApiError } from '@/lib/api';

// Threads and Profile are stubs -- their real UI is issues #15 and #14
// respectively. This layout exists so #11 delivers a real, navigable
// authenticated shell, not just a single placeholder screen.
//
// Uses expo-router's standard (JS-rendered) Tabs, not NativeTabs --
// that API ships from `expo-router/unstable-native-tabs`, and this
// project avoids building foundational navigation on anything the
// library itself labels unstable (same reasoning as the styling-engine
// choice in issue #11's scoping note). Revisit once it graduates.
//
// threads/[id] (issue #15's detail slice) and threads/new (issue #15's
// create slice) are real screen files under this same directory,
// reached via router.push from the thread list -- both are
// deliberately declared here too, with `href: null`. Without that,
// expo-router's Tabs auto-registers every matched route file as a tab
// (not just the ones explicitly declared), so leaving either out would
// put an unwanted, unstyled extra tab in the bar. `href: null` keeps
// them reachable, non-tab-bar screens inside this same Tabs navigator
// -- a full nested-Stack-per-tab restructure (the documented ideal for
// "push a detail screen over a tab") is a bigger change than this
// slice needs; revisit only if a real need for a proper
// stack-push-over-tab-bar transition shows up.
// profile, threads/new, AND threads/[id] all set headerShown: false --
// their default React Navigation header duplicated their own in-body
// heading ("Create your profile", "New thread", and now the real
// thread's own title). threads/[id] used to keep its default header on
// the theory that it had no competing in-body heading -- true when this
// comment was written, false since ThreadDetail's ThreadHeader grew a
// real `<Heading>{thread.title}</Heading>` (issue #15): the native
// header then showed the hardcoded literal word "Thread" directly above
// the thread's actual, real title, reading as an unpolished placeholder
// (real user feedback on the redeployed app, 2026-09-04 -- this route's
// static Tabs.Screen `title: 'Thread'` can't be made dynamic per-thread
// without a bigger nested-stack restructure this slice doesn't need,
// so hiding it was the smaller, self-consistent fix, matching how this
// same file already treats profile/threads/new). index keeps its
// default header: it has no competing in-body page heading. Matches the
// root _layout.tsx's own headerShown: false pattern, applied only where
// it doesn't remove functionality this app still needs -- see
// screen-container.tsx's topInset doc comment for the other half of
// this (profile/create-thread now default to topInset={true} instead
// of relying on a header for safe-area padding), and
// src/screens/create-thread/index.tsx's own in-body Cancel link and
// src/screens/thread-detail/index.tsx's own in-body Back link, which
// replace the back button this change removes from threads/new and
// threads/[id] respectively.
//
// The tab bar's chrome -- tints, label/item style objects, the glass
// TabBarBackground, and the reverse-engineered web height formula --
// lives in components/tab-bar.tsx, moved there verbatim (numeric
// values and comments included; its history is #269/#297 x8). This
// file is composition only: registrations, badges, drawer, debug
// overlay.
function matchesLabel(unreadConversations: number, requests: number): string {
  const parts = [
    unreadConversations > 0 &&
      `${unreadConversations} conversation${unreadConversations === 1 ? '' : 's'} with unread messages`,
    requests > 0 && `${requests} pending reveal request${requests === 1 ? '' : 's'}`,
  ].filter(Boolean);

  return parts.length > 0 ? `Matches, ${parts.join(', ')}` : 'Matches';
}

function AppTabs() {
  const screenOptions = useTabBarScreenOptions();

  // Both tab badges (issue #395) ride the ONE app-wide /me/activity
  // poll -- 30s while visible, 304-on-If-None-Match as the steady
  // state, last values kept on failure. See use-activity.ts; the same
  // query also feeds the threads screen's banner and the settings
  // screen's link count.
  const activity = useActivity();
  // Decision 6: the Matches badge counts conversations with something unread plus pending requests.
  const unreadConversationCount = activity.data?.summary.unreadConversations ?? 0;
  const incomingRequestCount = activity.data?.summary.pendingRevealRequests ?? 0;

  // No profile yet means the first-run wizard is on screen: Threads and
  // Matches would only lead to a "create a profile" dead end, so the bar
  // stays out of the way until step 1 creates the profile.
  const profile = useMyProfile();
  const firstRun = profile.isError && profile.error instanceof ApiError && profile.error.status === 404;

  const dockHeight = useDockHeight();
  useEffect(() => setDockHideDistance(dockHeight + 24), [dockHeight]);

  return (
    <DockInsetContext.Provider value={firstRun ? 0 : dockHeight}>
      <Tabs
        screenOptions={
          firstRun ? { ...screenOptions, tabBarStyle: [screenOptions.tabBarStyle, { display: 'none' }] } : screenOptions
        }
      >
        <Tabs.Screen
          name="index"
          options={{
            title: 'Threads',
            // headerShown: false, matching every other tab screen's own
            // convention -- founder, 2026-09-16, adversarial-lens pass:
            // "the threads font at the top of the screen is ugly (and
            // not sure if the screen needs a name up there)". Root
            // cause: this was the ONE remaining screen still using React
            // Navigation's own native header (see this file's own header
            // comment on why -- it used to carry this screen's safe-area
            // top padding), whose title text renders in the OS system
            // font, never this app's own Libre Franklin (navigationTheme
            // above only remaps COLORS, not fontFamily -- confirmed by
            // reading it, not assumed). The tab bar itself already
            // labels this screen "Threads" (icon + tabBarAccessibilityLabel
            // below), and the radius line + "New thread" button
            // immediately under this screen's own in-body content already
            // make it self-evident what's being browsed -- a second,
            // differently-fonted "Threads" title added nothing a real
            // user needed. ScreenContainer's own topInset picks up the
            // safe-area padding this screen no longer gets from the
            // native header (see threads/index.tsx's own ScreenContainer
            // call, switched from topInset={false} alongside this).
            headerShown: false,
            tabBarIcon: ({ color, size, focused }) => (
              <DockIcon focused={focused}>
                <ThreadIcon color={String(color)} size={iconSize.dock} strokeWidth={iconStroke} />
              </DockIcon>
            ),
            tabBarAccessibilityLabel: 'Threads',
          }}
        />
        <Tabs.Screen
          name="matches"
          options={{
            title: 'Matches',
            headerShown: false,
            tabBarBadge:
              unreadConversationCount + incomingRequestCount > 0
                ? unreadConversationCount + incomingRequestCount
                : undefined,
            tabBarIcon: ({ color, size, focused }) => (
              <DockIcon focused={focused}>
                <MatchesIcon color={String(color)} size={iconSize.dock} strokeWidth={iconStroke} />
              </DockIcon>
            ),
            tabBarAccessibilityLabel: matchesLabel(unreadConversationCount, incomingRequestCount),
          }}
        />
        <Tabs.Screen
          name="profile"
          options={{
            title: 'Profile',
            headerShown: false,
            tabBarIcon: ({ color, size, focused }) => (
              <DockIcon focused={focused}>
                <ProfileIcon color={String(color)} size={iconSize.dock} strokeWidth={iconStroke} />
              </DockIcon>
            ),
            tabBarAccessibilityLabel: 'Profile',
          }}
        />
        <Tabs.Screen name="threads/[id]" options={{ href: null, title: 'Thread', headerShown: false }} />
        <Tabs.Screen name="threads/new" options={{ href: null, title: 'New Thread', headerShown: false }} />
        <Tabs.Screen name="users/[userId]" options={{ href: null, title: 'Profile', headerShown: false }} />
        <Tabs.Screen name="reveal-requests" options={{ href: null, title: 'Reveal requests', headerShown: false }} />
        <Tabs.Screen name="safety-center" options={{ href: null, title: 'Safety Center', headerShown: false }} />
        <Tabs.Screen name="safety-reminder" options={{ href: null, title: 'Stay safe', headerShown: false }} />
        <Tabs.Screen name="settings" options={{ href: null, title: 'Settings', headerShown: false }} />
        <Tabs.Screen
          name="notification-settings"
          options={{ href: null, title: 'Notifications', headerShown: false }}
        />
        <Tabs.Screen name="profile-photos" options={{ href: null, title: 'Photos', headerShown: false }} />
        <Tabs.Screen name="profile-prompts" options={{ href: null, title: 'Prompts', headerShown: false }} />
        <Tabs.Screen name="block/[userId]" options={{ href: null, title: 'Block', headerShown: false }} />
        <Tabs.Screen name="blocked-users" options={{ href: null, title: 'Blocked users', headerShown: false }} />
        <Tabs.Screen
          name="report/[targetType]/[targetId]"
          options={{ href: null, title: 'Report', headerShown: false }}
        />
        <Tabs.Screen name="messages/[connectionId]" options={{ href: null, title: 'Messages', headerShown: false }} />
      </Tabs>
    </DockInsetContext.Provider>
  );
}

// AppDrawer is a sibling AFTER AppTabs (not nested inside), both
// children of AppDrawerProvider -- later siblings paint on top in RN's
// stacking order, so the drawer (and its scrim) covers the tab bar too
// without a manual z-index. AppDrawerProvider makes the open/close
// state (useAppDrawer) available to AppDrawer's own edge-swipe handler
// -- see that component's own doc comment for why the drawer has no
// visible trigger (founder, 2026-09-18: "we don't need a sidebar
// burger menu hint").
export default function AppLayout() {
  // TEMPORARY, see tab-bar-debug-overlay.tsx's own doc comment --
  // issue #297's 8th recurrence, gated behind ?debug=tabbar so it
  // never renders in the normal product. Delete both once #297 is
  // confirmed fixed on a real device.
  const showTabBarDebug =
    Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('debug') === 'tabbar';

  return (
    <AppDrawerProvider>
      <AppTabs />
      <AppDrawer />
      {showTabBarDebug && <TabBarDebugOverlay />}
    </AppDrawerProvider>
  );
}
